import { Form, Card, Button, Col, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import { toast } from 'react-toastify'
import { cookiesKeyNames } from '@revbox/common-fe'
import { validateImageDimensions } from '@revbox/helpers-fe'
import { useCookiesContext } from '../contexts/CookiesContext'
import { handleFormSubmit } from '../helpers/handle-signup-submit'
import { useDebouncedCallback } from '../hooks/useDebounceCallback';
import { logosModule } from '../modules/logos'
import { getPublisherCategoriesBySecret } from '../helpers/get-publisher-categories-by-secret'
import ModalOnboard from '../components/onboarding/ModalOnboard'
import LoaderWithBlur from '../components/LoaderWithBlur'
import { validateEmail } from '../helpers/validate-email-onboarding'
import ImageCropModal from '../adv/campaign/components/common/ImageCropModal'
import PageWithLoader from '../layouts/LoadTillStylesApply'

const labels = {
    first_name: 'first name',
    last_name: 'last name',
    email: 'email',
    company_name: 'company name',
    product_name: 'product name',
    product_url: 'product URL',
    logo: 'logo',
    main_category: 'category'
}

const validateForm = (formData, setValidationResults, invalidEmail) => {
    for (const [key, value] of Object.entries(formData)) {
        if (invalidEmail) {
            toast.error('Invalid Email. Please use a business email address')
            return false
        }
        if (!value) {
            toast.error(`Please enter your ${labels[key]}.`)
            setValidationResults(prev => ({
                ...prev,
                [key]: false
            }))
            return false
        }

        if (formData.main_category === null) {
            toast.error('Please select a category.')
            setValidationResults(prev => ({
                ...prev,
                main_category: false
            }))
            return false
        }
        setValidationResults(prev => ({
            ...prev,
            [key]: true
        }))
    }
    return true
}

const fetchCategories = async () => {
    const nnx_id = 34
    const result = await getPublisherCategoriesBySecret(nnx_id)

    if (result) {
        const formatedCategories = result.data.map(category => ({
            label: category.name,
            value: category.id
        }))
        return formatedCategories
    }
}

export const Onboarding = () => {
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [productName, setProductName] = useState('')
    const [productUrl, setProductUrl] = useState('')
    const [category, setCategory] = useState(null)
    const [categories, setCategories] = useState([])
    const [siteLogo, setSiteLogo] = useState(null)
    const [validationResults, setValidationResults] = useState({})

    const [imageFiles, setImageFiles] = useState(null)
    const [dimensions, setDimensions] = useState({ width: 100, height: 100 })
    const [croppedLogoPreview, setCroppedLogoPreview] = useState(null)
    const [croppedLogoFile, setCroppedLogoFile] = useState(null)

    const [isLoading, setIsLoading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isCropImageModalShow, setIsCropImageModalShow] = useState(false)
    const [modalMessage, setModalMessage] = useState('')
    const [modalStatus, setModalStatus] = useState('')

    const [invalidEmail, setInvalidEmail] = useState(false)

    const [ref, setRef] = useState(null)

    const publisherByAbbr = window.location.search.split('=')[1]

    const logoInputRef = useRef(null)
    const logoDimensions = {
        width: publisherByAbbr?.logo_width_dimension ?? 150,
        height: publisherByAbbr?.logo_height_dimension ?? 150
    }

    const { cookiesData, getPathKey, updateCookiesProperty } = useCookiesContext()

    const cookiesDataByPath = cookiesData[getPathKey()]

    useEffect(() => {
        const skinMode = cookiesDataByPath?.[cookiesKeyNames.skinMode]
        if (skinMode) {
            updateCookiesProperty(cookiesKeyNames.skinMode, skinMode)
        }

        const siteLogo = logosModule(publisherByAbbr)

        setSiteLogo(siteLogo)

        fetchCategories().then(categories => {
            if (!categories) {
                toast.error('Failed to fetch categories')
                return
            }
            setCategories(categories)
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publisherByAbbr])

    const debouncedValidate = useDebouncedCallback(async (value) => {
        if (!value.includes('@') || value.endsWith('@')) return
      
        const domainPart = value.split('@')[1]
        if (!domainPart || domainPart.length < 3 || !domainPart.includes('.')) return
      
        const { validationResults, invalidEmail } = await validateEmail(value)
        setValidationResults((prev) => ({ ...prev, ...validationResults }))
        setInvalidEmail(invalidEmail)
      }, 400)
    
    const onEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        debouncedValidate(value);
    };

    const onModalClose = () => {
        setIsModalOpen(false)
        setModalMessage('')
        setModalStatus('')
    }

    const modalHandleClose = () => setIsCropImageModalShow(false)

    const onLogoChange = async e => {
        if (e.target.files.length > 0) {
            const files = Array.from(e.target.files)

            const { validFiles } = await validateImageDimensions(files, logoDimensions)

            if (validFiles.length > 0) {
                setRef(logoInputRef)
                setImageFiles(validFiles)
                setDimensions(logoDimensions)
                setIsCropImageModalShow(true)
            }

            e.target.value = null
        }
    }

    const onSubmit = e => {
        e.preventDefault()
        const formData = {
            first_name: firstName,
            last_name: lastName,
            email,
            company_name: companyName,
            product_name: productName,
            product_url: productUrl,
            main_category: category?.value,
            owner_id: 34, //Netnetix family
            logo: croppedLogoFile
        }
        const isValid = validateForm(formData, setValidationResults, invalidEmail)
        if (!isValid) {
            return
        }

        handleFormSubmit({
            formData,
            setIsLoading,
            setIsModalOpen,
            setModalMessage,
            setModalStatus,
            setValidationResults
        })
    }

    const handleSaveCroppedImages = croppedFile => {
        const objectUrl = URL.createObjectURL(croppedFile)

        if (ref === logoInputRef) {
            setCroppedLogoFile(croppedFile)
            setCroppedLogoPreview(objectUrl)
            document.getElementById('logo-input-placeholder').innerText = 'Logo loaded'
        }
    }

    return (
        <PageWithLoader>
        <Form className="page-sign" onSubmit={onSubmit}>
            {isModalOpen && (
                <ModalOnboard isModalOpen={isModalOpen} onModalClose={onModalClose} modalStatus={modalStatus} modalMessage={modalMessage} />
            )}
            {isLoading && (
                <LoaderWithBlur 
                    publisherByAbbr={publisherByAbbr} 
                    content="We're processing your request. This may take a minute or so." 
                />
            )}
            {isCropImageModalShow && (
                <ImageCropModal
                modalShow={isCropImageModalShow}
                modalHandleClose={modalHandleClose}
                imageFiles={imageFiles}
                onSave={handleSaveCroppedImages}
                dimensions={dimensions}
                ref={ref}
            />
            )}
            <Card className="card-sign" style={{ width: '620px' }}>
                <Card.Header>
                    <Link to="/" className="header-logo">
                        {siteLogo}
                    </Link>
                    <Card.Title className="mb-4">Advertiser Sign Up</Card.Title>
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6} className="p-1">
                            <Form.Label>First name</Form.Label>
                            <Form.Control
                                onChange={e => {
                                    setFirstName(e.target.value)
                                }}
                                value={firstName}
                                type="text"
                                placeholder="Enter your first name*"
                                isInvalid={validationResults.first_name === false}
                            />
                        </Col>
                        <Col md={6} className="p-1">
                            <Form.Label>Last name</Form.Label>
                            <Form.Control
                                onChange={e => {
                                    setLastName(e.target.value)
                                }}
                                value={lastName}
                                type="text"
                                placeholder="Enter your last name*"
                                isInvalid={validationResults.last_name === false}
                            />
                        </Col>
                        <Col md={6} className="p-1">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                onChange={onEmailChange}
                                value={email}
                                type="email"
                                placeholder="Enter your email address*"
                                isInvalid={validationResults.email === false}
                            />
                        </Col>
                        <Col md={6} className="p-1">
                            <div className="col">
                                <small
                                    className="mt-4 onboarding-border"
                                    style={{
                                        borderLeft: 'solid 3px',
                                        padding: '5px 7px'
                                    }}
                                >
                                    Please use your company email, so we can verify that your company owns the product.
                                </small>
                            </div>
                        </Col>
                        <hr style={{ marginBottom: 0 }} />
                        <Col md={12} className="p-1" style={{ marginTop: '0.25rem' }}>
                            <Form.Label>Company name</Form.Label>
                            <Form.Control
                                onChange={e => {
                                    setCompanyName(e.target.value)
                                }}
                                value={companyName}
                                type="text"
                                placeholder="Enter your company name*"
                                isInvalid={validationResults.company_name === false}
                            />
                        </Col>
                        <hr style={{ marginBottom: 0 }} />
                        <Col md={6} className="p-1" style={{ marginTop: '0.25rem' }}>
                            <Form.Label>Product name</Form.Label>
                            <Form.Control
                                onChange={e => {
                                    setProductName(e.target.value)
                                }}
                                value={productName}
                                type="text"
                                placeholder="Enter your product name*"
                                isInvalid={validationResults.product_name === false}
                            />
                        </Col>
                        <Col md={6} className="p-1" style={{ marginTop: '0.25rem' }}>
                            <Form.Label>Product url</Form.Label>
                            <Form.Control
                                onChange={e => {
                                    setProductUrl(e.target.value)
                                }}
                                value={productUrl}
                                type="text"
                                placeholder="Enter your product url*"
                                isInvalid={validationResults.product_url === false}
                            />
                        </Col>
                        <Col md={12} className="p-1" style={{ marginTop: '0.25rem' }}>
                            <Form.Label>Product logo</Form.Label>

                            <form
                                className="file-input-form"
                                method="post"
                                encType="multipart/form-data"
                                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}
                            >
                                <Col md={8}>
                                    <div className="input-file-preview" style={{ marginBottom: 0 }}>
                                        <label htmlFor="logo_upload" style={{ lineHeight: '38px' }}>
                                            Upload Logo
                                        </label>
                                        <p className="vertical-stick" style={{ marginBottom: 0, lineHeight: '38px', padding: 0 }}></p>
                                        <p id="logo-input-placeholder" style={{ marginBottom: 0, lineHeight: '38px', paddingRight: '10px' }}>
                                            No file currently selected for upload
                                        </p>
                                    </div>
                                </Col>
                                <div>
                                    <input
                                        type="file"
                                        id="logo_upload"
                                        name="image_uploads"
                                        accept=".png,.jpg,.jpeg"
                                        onChange={onLogoChange}
                                        ref={logoInputRef}
                                        style={{ opacity: 0, width: 0 }}
                                    />
                                </div>
                                {croppedLogoPreview && (
                                    <Col md={4}>
                                        <div className="cropped-image-preview" style={{ marginTop: 0, marginLeft: 'auto' }}>
                                            <img src={croppedLogoPreview} alt="Logo Preview" style={{ width: '150px', height: '150px' }} />
                                        </div>
                                    </Col>
                                )}
                            </form>
                        </Col>
                        <Col md={6} className="p-1" style={{ marginTop: '0.25rem' }}>
                            <Form.Label>Main category</Form.Label>

                            <Select
                                className="custom-select"
                                id="basic-select-single"
                                value={category}
                                placeholder={'Pick your category*'}
                                options={categories}
                                onChange={selectedOption => setCategory(selectedOption)}
                                isInvalid={validationResults.main_category === false}
                            />
                        </Col>
                        <Col md={6} className="p-1" style={{ marginTop: '0.25rem' }}>
                            <div className="col">
                                <small
                                    className="mt-4 onboarding-border"
                                    style={{
                                        borderLeft: 'solid 3px',
                                        padding: '5px 7px'
                                    }}
                                >
                                    Pick the category that best describes your product. You can add more later.
                                </small>
                            </div>
                        </Col>
                    </Row>
                    <div className="mt-2 mb-3">
                        <small>
                            By applying for access below, you agree to our{' '}
                            <a href="https://www.netnetix.com/terms" target="_blank" rel="noopener noreferrer">
                                advertiser policy
                            </a>{' '}
                            and{' '}
                            <a href="/" target="_blank" rel="noopener noreferrer">
                                privacy policy
                            </a>.
                            {/* The application review is AI powered - you will get an immediate answer if your product is elegible. */}
                        </small>
                    </div>
                    <hr />
                    <Button type="submit" variant="primary" className="btn-sign">
                        Apply for access
                    </Button>
                </Card.Body>
                <Card.Footer style={{ padding: '35px' }}>
                    Already have an account? <a href="/pages/signin">Sign In</a> instead.
                </Card.Footer>
            </Card>
        </Form>
        </PageWithLoader>
    )
}
